import React, { useState, useEffect } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  TextField, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Tooltip, Select, MenuItem, FormControl, InputLabel,
  TablePagination, Box, Typography, Chip, Avatar
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon } from '@mui/icons-material';
import { register, getUsers, updateUser, deleteUser } from '../services/api';
import { styled } from '@mui/material/styles';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  '&.MuiTableCell-head': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterField, setFilterField] = useState('all');
  const [filterValue, setFilterValue] = useState('');
  const [open, setOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newUser, setNewUser] = useState({
    email: '',
    username: '',
    user_group: '',
    vip_expires_at: '',
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    applyFilter();
  }, [users, filterField, filterValue]);

  const fetchUsers = async () => {
    try {
      const response = await getUsers();
      setUsers(response.data);
      setFilteredUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const applyFilter = () => {
    if (filterField === 'all') {
      setFilteredUsers(users.filter(user =>
        Object.values(user).some(value => 
          String(value).toLowerCase().includes(filterValue.toLowerCase())
        )
      ));
    } else if (filterField === 'created_at') {
      setFilteredUsers(users.filter(user =>
        new Date(user.created_at).toLocaleString().includes(filterValue)
      ));
    } else {
      setFilteredUsers(users.filter(user =>
        String(user[filterField]).toLowerCase().includes(filterValue.toLowerCase())
      ));
    }
    setPage(0);
  };

  const handleFilterFieldChange = (event) => {
    setFilterField(event.target.value);
  };

  const handleFilterValueChange = (event) => {
    setFilterValue(event.target.value);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const value = parseInt(event.target.value, 10);
    setRowsPerPage(value === -1 ? filteredUsers.length : value);
    setPage(0);
  };

  const handleOpen = (user = null) => {
    setEditingUser(user);
    setNewUser(user || {
      email: '',
      username: '',
      password: '',
      user_group: '',
      vip_expires_at: ''
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingUser(null);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setNewUser(prev => ({
      ...prev,
      [name]: name === 'vip_expires_at' ? value ? new Date(value).toISOString() : null : value
    }));
  };

  const handleSubmit = async () => {
    try {
      const userData = {
        ...newUser,
        vip_expires_at: newUser.vip_expires_at ? new Date(newUser.vip_expires_at).toISOString() : null,
        isTopuser: true
      };
      if (editingUser) {
        await updateUser(editingUser.id, userData);
      } else {
        await register(userData);
      }
      fetchUsers();
      handleClose();
    } catch (error) {
      console.error('Error saving user:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await deleteUser(id);
        fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
      }
    }
  };

  return (
    <Box sx={{ width: '100%', mb: 4 }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
        用户管理 ({users.length})
      </Typography>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center">
          <FormControl variant="outlined" sx={{ minWidth: 120, mr: 2 }}>
            <InputLabel>筛选条件</InputLabel>
            <Select
              value={filterField}
              onChange={handleFilterFieldChange}
              label="筛选条件"
            >
              <MenuItem value="all">所有字段</MenuItem>
              <MenuItem value="email">邮箱</MenuItem>
              <MenuItem value="username">用户名</MenuItem>
              <MenuItem value="user_group">用户组</MenuItem>
              <MenuItem value="created_at">创建时间</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="搜索"
            variant="outlined"
            value={filterValue}
            onChange={handleFilterValueChange}
            InputProps={{
              endAdornment: (
                <IconButton>
                  <SearchIcon />
                </IconButton>
              ),
            }}
          />
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
        >
          添加用户
        </Button>
      </Box>
      <TableContainer component={Paper} elevation={3}>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell>ID</StyledTableCell>
              <StyledTableCell>邮箱</StyledTableCell>
              <StyledTableCell>用户名</StyledTableCell>
              <StyledTableCell>用户组</StyledTableCell>
              <StyledTableCell>创建时间</StyledTableCell>
              <StyledTableCell>创建IP</StyledTableCell>
              <StyledTableCell>VIP到期时间</StyledTableCell>
              <StyledTableCell>操作</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(rowsPerPage > 0
              ? filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              : filteredUsers
            ).map((user) => (
              <StyledTableRow key={user.id}>
                <TableCell>{user.id}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ mr: 1, width: 24, height: 24 }}>{user.username[0]}</Avatar>
                    {user.username}
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={user.user_group}
                    color={user.user_group === 'admin' ? 'error' : user.user_group === 'vip' ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>{new Date(user.created_at).toLocaleString()}</TableCell>
                <TableCell>{user.created_ip}</TableCell>
                <TableCell>
                  {user.vip_expires_at ? new Date(user.vip_expires_at).toLocaleString() : '非VIP'}
                </TableCell>
                <TableCell>
                  <Tooltip title="编辑">
                    <IconButton onClick={() => handleOpen(user)} color="primary">
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="删除">
                    <IconButton onClick={() => handleDelete(user.id)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
        component="div"
        count={filteredUsers.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="每页行数"
      />

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{editingUser ? '编辑用户' : '添加用户'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="email"
            label="邮箱地址"
            type="email"
            fullWidth
            variant="standard"
            value={newUser.email}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            name="username"
            label="用户名"
            type="text"
            fullWidth
            variant="standard"
            value={newUser.username}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            name="password"
            label="密码"
            type="password"
            fullWidth
            variant="standard"
            value={newUser.password}
            onChange={handleInputChange}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>用户组</InputLabel>
            <Select
              name="user_group"
              value={newUser.user_group}
              onChange={handleInputChange}
            >
              <MenuItem value="admin">管理员</MenuItem>
              <MenuItem value="regular">普通用户</MenuItem>
              <MenuItem value="vip">VIP用户</MenuItem>
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            name="vip_expires_at"
            label="VIP到期时间"
            type="datetime-local"
            fullWidth
            variant="standard"
            InputLabelProps={{
              shrink: true,
            }}
            value={newUser.vip_expires_at ? new Date(newUser.vip_expires_at).toISOString().slice(0, 16) : ''}
            onChange={handleInputChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>取消</Button>
          <Button onClick={handleSubmit}>{editingUser ? '更新' : '添加'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserList;